/**
 * Connecting WhatsApp, through Chatlio, with the template list read from Meta.
 *
 * Three things have to be true, and the card says which are: Chatlio accepts
 * our token (sending), Chatlio has our webhook (replies and receipts), and
 * Meta lets us read the account (the template list). Once all three are, the
 * card is one green line and a button to open the details; it is a form only
 * while something still needs filling in.
 *
 * The tokens are stored on the server beside the mail passwords and never
 * come back to the browser. A saved form with a token box empty keeps the one
 * that is there.
 */
import { useEffect, useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import api from '../../lib/api.js';
import { isOffline } from '../../lib/storage.js';
import Icon from '../../lib/icons.jsx';
import { Spinner } from '../../components/Primitives.jsx';

const BLANK = {
  baseUrl: 'https://chatlio.io/whatsapp/public/api',
  vendorUid: '',
  token: '',
  phoneId: '',
  phone: '',
  name: '',
  enabled: true,
  wabaId: '',
  metaToken: ''
};

export default function WhatsAppSetup() {
  const { toast } = useApp();

  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState('');
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const take = out => {
    setSaved(out);
    setForm({ ...BLANK, ...out, token: '', metaToken: '' });
  };

  useEffect(() => {
    if (isOffline()) return;
    api.whatsapp.setup().then(take).catch(e => toast(e.message));
  }, [toast]);

  const run = async (kind, fn) => {
    setBusy(kind);
    try { await fn(); } catch (e) { toast(e.message); }
    setBusy('');
  };

  const test = () => run('test', async () => {
    try {
      const out = await api.whatsapp.testSetup({ vendorUid: form.vendorUid, token: form.token || undefined });
      toast(`Chatlio — ${out.reason}`);
      setSaved(s => ({ ...(s || {}), lastTest: { ok: true, reason: out.reason, at: new Date().toISOString() } }));
    } catch (e) {
      setSaved(s => ({ ...(s || {}), lastTest: { ok: false, reason: e.message, at: new Date().toISOString() } }));
      throw e;
    }
  });

  const testMeta = () => run('meta', async () => {
    try {
      const out = await api.whatsapp.testMeta({ wabaId: form.wabaId, metaToken: form.metaToken || undefined });
      toast(`Meta — ${out.reason}`);
      setSaved(s => ({ ...(s || {}), lastMetaTest: { ok: true, reason: out.reason, at: new Date().toISOString() } }));
    } catch (e) {
      setSaved(s => ({ ...(s || {}), lastMetaTest: { ok: false, reason: e.message, at: new Date().toISOString() } }));
      throw e;
    }
  });

  const save = () => run('save', async () => {
    if (!form.vendorUid.trim()) throw new Error('The vendor UID is on Chatlio → Settings → API Access.');
    if (!form.token.trim() && !(saved && saved.hasToken)) throw new Error('Paste the Chatlio API token.');
    take(await api.whatsapp.saveSetup(form));
    toast('Saved');
  });

  const copyHook = () => {
    if (!saved || !saved.hookUrl || !navigator.clipboard) return;
    navigator.clipboard.writeText(saved.hookUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };

  if (isOffline()) return <p className="muted">Sign in to connect WhatsApp.</p>;
  if (!saved) return <p className="muted"><Spinner /> Loading…</p>;

  const chatlioOk = !!(saved.hasToken && saved.vendorUid && saved.enabled
    && !(saved.lastTest && saved.lastTest.ok === false));
  const metaOk = !!(saved.hasMetaToken && saved.wabaId
    && !(saved.lastMetaTest && saved.lastMetaTest.ok === false));
  const hookReady = !!saved.hookUrl;
  const allGood = chatlioOk && metaOk && hookReady;

  const showForm = open || !allGood;

  return (
    <div className="wasetup">
      <div className={`wastatus ${allGood ? 'ok' : chatlioOk ? 'warn' : 'off'}`}>
        <span className="wadot" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <b>
            {chatlioOk
              ? `Connected through Chatlio${saved.phone ? ` as ${saved.phone}` : ''}`
              : 'Not connected'}
          </b>
          <small className="waticks">
            <Tick ok={chatlioOk} what="Sending" />
            <Tick ok={hookReady} what="Webhook" />
            <Tick ok={metaOk} what="Template list" />
          </small>
        </div>
        {allGood ? (
          <button type="button" className="btn sm" onClick={() => setOpen(o => !o)}>
            <Icon name="gear" />{open ? 'Hide' : 'Details'}
          </button>
        ) : null}
      </div>

      {showForm ? (
        <>
          <h5 className="wah">Chatlio</h5>
          <div className="form">
            <div className="f">
              <label>Vendor UID</label>
              <input value={form.vendorUid} onChange={e => set('vendorUid', e.target.value.trim())} />
            </div>
            <div className="f">
              <label>API access token</label>
              <input
                type="password"
                value={form.token}
                placeholder={saved.hasToken ? '•••••••• saved — blank keeps it' : 'from Chatlio → Settings'}
                onChange={e => set('token', e.target.value.trim())}
                autoComplete="new-password"
              />
            </div>
            <div className="f">
              <label>The number</label>
              <input value={form.phone} placeholder="+971 50 695 3496" onChange={e => set('phone', e.target.value)} />
            </div>
            <div className="f">
              <label>Phone Number ID <span className="dim">(learned automatically)</span></label>
              <input value={form.phoneId} onChange={e => set('phoneId', e.target.value.trim())} />
            </div>
          </div>
          <div className="rowb" style={{ marginTop: 8 }}>
            <button type="button" className="btn sm" onClick={test} disabled={!!busy}>
              {busy === 'test' ? <><Spinner />Testing…</> : <><Icon name="zap" />Test Chatlio</>}
            </button>
            <label className="waon">
              <input type="checkbox" checked={form.enabled !== false} onChange={e => set('enabled', e.target.checked)} />
              Switched on
            </label>
          </div>

          <h5 className="wah">Webhook — paste into Chatlio → Settings → Webhook Endpoint</h5>
          {hookReady ? (
            <div className="wahook">
              <code>{saved.hookUrl}</code>
              <button type="button" className="btn sm" onClick={copyHook}>
                <Icon name="check" />{copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          ) : (
            <p className="fhint" style={{ margin: 0 }}>Minted when you save.</p>
          )}

          <h5 className="wah">Meta — read-only, for the template list</h5>
          <div className="form">
            <div className="f">
              <label>WhatsApp Business Account ID</label>
              <input
                value={form.wabaId}
                placeholder="WhatsApp Manager → Overview"
                onChange={e => set('wabaId', e.target.value.trim())}
              />
            </div>
            <div className="f">
              <label>System User token</label>
              <input
                type="password"
                value={form.metaToken}
                placeholder={saved.hasMetaToken ? '•••••••• saved — blank keeps it' : 'EAA…'}
                onChange={e => set('metaToken', e.target.value.trim())}
                autoComplete="new-password"
              />
            </div>
          </div>
          <div className="rowb" style={{ marginTop: 8 }}>
            <button type="button" className="btn sm" onClick={testMeta} disabled={!!busy}>
              {busy === 'meta' ? <><Spinner />Testing…</> : <><Icon name="zap" />Test Meta</>}
            </button>
            {saved.lastMetaTest ? (
              <span className="fhint" style={{ margin: 0 }}>{saved.lastMetaTest.reason}</span>
            ) : null}
          </div>

          <div className="rowb" style={{ marginTop: 14 }}>
            <button type="button" className="btn sm primary" onClick={save} disabled={!!busy}>
              {busy === 'save' ? <><Spinner />Saving…</> : <><Icon name="check" />Save</>}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

/** One word and a mark: what is working and what is not, at a glance. */
function Tick({ ok, what }) {
  return (
    <span className={`watick${ok ? ' ok' : ''}`}>
      <Icon name={ok ? 'check' : 'x'} />{what}
    </span>
  );
}
